////////////////////////////////////////////////////////////////////////////////////////////
// New Frontend Code ///////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////
import Alpine from '@alpinejs/csp';
import Focus from '@alpinejs/focus';
import collapse from '@alpinejs/collapse';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

declare global {
    interface Window {
        Alpine: typeof Alpine;
    }
}

window.t2i = window.t2i || {};

window.Alpine = Alpine;
Alpine.plugin(collapse);
Alpine.plugin(Focus);
gsap.registerPlugin(ScrollTrigger);

type EvalPrompt = {
    id: number;
    value: string;
    isVisible: boolean;
    imageDataUrl?: string;
    imageName?: string;
    imageS3Key?: string;
    imageUploading?: boolean;
    imageUploadError?: string;
    question?: string;
};

// One parsed prompt from /_upload-prompts/?format=json (CSV/ZIP import).
type UploadedPrompt = {
    text: string;
    image_s3_key: string | null;
    image_name: string | null;
    image_url: string | null;
    question: string | null;
};

type CustomModel = {
    id: string;
    name: string;
    blurb: string;
    // Same model as another provider's entry — hidden from browse/search discovery,
    // but still resolvable (e.g. an existing eval that selected it).
    redundant: boolean;
    publisher: string;
    elo: string;
    elo_value: number | null;
    released: string;
    released_value: string;
    cost: string;
    cost_usd: number;
    supports_image_input: boolean;
    open_weights: boolean;
};

// Forgiving match for model search: "sd3.5", "SD 3.5" and "sd-3-5" all hit
// "SD 3.5 Large". Compare lowercase alphanumerics only.
function normalizeModelSearch(value: string): string {
    return value.toLowerCase().replace(/[^a-z0-9]/g, '');
}

// ─── Browse-modal table sort ────────────────────────────────────────────────
// Sorts stack: the clicked column becomes primary and the previous rules stay
// on as tiebreakers, so "sort by Elo, then by Publisher" groups by publisher
// with each publisher's models still ordered by Elo.
type BrowseSortKey = 'name' | 'publisher' | 'elo' | 'released' | 'open_weights' | 'cost';
type BrowseSortRule = { key: BrowseSortKey; dir: 'asc' | 'desc' };

// First click on a column sorts the way that column is usually wanted.
const BROWSE_SORT_DEFAULT_DIR: Record<BrowseSortKey, 'asc' | 'desc'> = {
    name: 'asc',
    publisher: 'asc',
    elo: 'desc',
    released: 'desc',
    open_weights: 'desc',
    cost: 'asc',
};

// How many past clicks stay on as tiebreakers.
const BROWSE_SORT_DEPTH = 3;

// null = unknown; unknowns sink to the bottom in both directions.
function browseSortValue(model: CustomModel, key: BrowseSortKey): string | number | null {
    switch (key) {
        case 'name':
            return model.name.toLowerCase();
        case 'publisher':
            return model.publisher.toLowerCase();
        case 'elo':
            return model.elo_value;
        case 'released':
            return model.released_value || null;
        case 'open_weights':
            return model.open_weights ? 1 : 0;
        case 'cost':
            return model.cost_usd;
    }
}

function compareBrowseModels(a: CustomModel, b: CustomModel, rule: BrowseSortRule): number {
    const av = browseSortValue(a, rule.key);
    const bv = browseSortValue(b, rule.key);
    if (av === null || bv === null) {
        if (av === bv) return 0;
        return av === null ? 1 : -1;
    }
    const order =
        typeof av === 'string' && typeof bv === 'string'
            ? av.localeCompare(bv)
            : (av as number) - (bv as number);
    if (order === 0) return 0;
    return rule.dir === 'asc' ? order : -order;
}

// A seeded prompt in the edit flow carries its text plus any attached
// reference image and per-prompt question (create seeds are plain strings).
type InitialPrompt = {
    text: string;
    imageS3Key?: string | null;
    imageUrl?: string | null;
    question?: string | null;
};

type FlowInitialState = {
    activeStep?: number;
    appliedModelGroup?: string;
    selectedModelIds?: string[];
    evalName?: string;
    prompts?: (string | InitialPrompt)[];
    imagesPerModel?: string;
    isPublic?: boolean;
    // Eval-side seed fields (clone flow only — eval_clone view). evalType is
    // always present; the rest are per-type (pairwise vs survey).
    evalType?: string;
    judge?: string;
    comparisonsPerModel?: number;
    questionPreset?: string;
    questionCustom?: string;
    surveyQuestionsJson?: string;
    responsesPerImage?: number;
};

// Present only in the clone flow (eval_clone view); identifies the parent
// dataset the clone forks from.
type EditParent = {
    slug: string;
    name: string;
    images_expired: boolean;
};

type FlowData = {
    groups: Record<string, string[]>;
    models: CustomModel[];
    initial_state?: FlowInitialState;
    edit_parent?: EditParent;
    // Survey editor seed (Evaluation step) — flow_data in _build_flow_data.
    survey_presets?: SurveyPreset[];
    survey_limits?: SurveyLimits;
};

// The generation half of calculate_run_price()['generation'] — the full child
// grid (num_images) plus, on a clone, the reused/new split.
type RunGeneration = {
    num_images: number;
    image_cost_cents: number;
    total_cents: number;
    num_new_images?: number;
    num_reused_images?: number;
};

// The rating half of calculate_run_price()['evaluation'] (null for images-only).
// Pairwise carries num_comparisons; survey carries num_responses instead.
type RunEvaluation = {
    rating_cost_cents: number;
    total_cents: number;
    evaluation_label: string;
    num_comparisons?: number;
    num_responses?: number;
};

// calculate_run_price() from POST /_run-price-preview/ — the unified create AND
// clone price. The Stripe floor is applied once to total_cents (component dicts
// stay pre-floor). evaluation is null / evaluation_label is null for images-only.
type RunPrice = {
    generation: RunGeneration;
    evaluation: RunEvaluation | null;
    image_cost_cents: number;
    rating_cost_cents: number;
    total_cents: number;
    num_new_images: number;
    num_reused_images: number;
    evaluation_label: string | null;
};

// Subset of calculate_eval_price() / calculate_survey_eval_price() from the
// add-comparisons preview (/_add-comparisons-price-preview/). Pairwise responses
// carry num_comparisons; survey responses carry num_responses instead — each is
// absent for the other eval type.
type EvalPrice = {
    num_comparisons?: number;
    num_responses?: number;
    rating_cost_cents: number;
    total_cents: number;
    evaluation_label: string;
};

// A survey quick-add preset chip (flow_data.survey_presets).
type SurveyPreset = { key: string; label: string; question: string };

// Validation limits shared with the server (flow_data.survey_limits).
type SurveyLimits = {
    max_per_slate: number;
    max_length: number;
    responses_min: number;
    responses_max: number;
    responses_default: number;
};


// Light/Dark Theme ///////////////////////////////////////////////////////////////////////
const isDark = localStorage.getItem('eval-theme') === 'dark';
document.documentElement.classList.toggle('dark', isDark);
document.documentElement.setAttribute('data-bs-theme', isDark ? 'dark' : 'light');

Alpine.store('theme', {
    dark: isDark,
    toggle(): void {
        this.dark = !this.dark;
        localStorage.setItem('eval-theme', this.dark ? 'dark' : 'light');
        document.documentElement.classList.toggle('dark', this.dark);
        document.documentElement.setAttribute('data-bs-theme', this.dark ? 'dark' : 'light');
        window.dispatchEvent(new CustomEvent('eval:theme-change'));
    }
});

// Mobile Navigation Menu
Alpine.data('mobileMenu', () => ({
    isOpen: false,
    isLayerActive: false,
    closeTimer: null as number | null,
    openMenu(): void {
        if (this.closeTimer) {
            window.clearTimeout(this.closeTimer);
            this.closeTimer = null;
        }

        this.isLayerActive = true;
        this.isOpen = true;
    },
    closeMenu(): void {
        this.isOpen = false;
        this.closeTimer = window.setTimeout(() => {
            this.isLayerActive = false;
            this.closeTimer = null;
        }, 500);
    },
}));


// Dataset Flow //////////////////////////////////////////////////////////////////////////
// The unified landing-page create flow. Collects a name, prompts, models, and
// per-model image count (generation half), THEN the evaluation config (Step 4:
// eval type + judge/question/comparisons or survey statements, or images-only),
// and pays for the whole run in one Stripe checkout. Server price == the charge.
Alpine.data('datasetFlow', () => ({
    // Step state
    activeStep: 1,
    hasPromptContent: false,

    // Models panel state. Catalog + groups are populated from #flow-data JSON
    // in init(). The selection itself is the state: preset "seeds" fill it in
    // one click; chips, search and the browse modal edit it in place.
    appliedModelGroup: '',
    customModelsOpen: false,
    customModels: [] as CustomModel[],
    modelGroups: {} as Record<string, string[]>,
    selectedCustomModelIds: [] as string[],
    pendingCustomModelIds: [] as string[],
    modelSearchQuery: '',
    modelSearchOpen: false,
    browseQuery: '',
    browseOpenWeightsOnly: false,
    browseSortRules: [] as BrowseSortRule[],
    openGroupInfoKey: '',
    // Mobile browse list: which model's blurb is tap-expanded inline (one at a time).
    openModelInfoId: '',
    // Desktop browse table: floating blurb tooltip shown on hover/focus of the ⓘ.
    // Fixed-positioned (coords computed from the icon) so the table's overflow-auto
    // can't clip it.
    modelTooltipVisible: false,
    modelTooltipText: '',
    modelTooltipLeft: 0,
    modelTooltipTop: 0,
    modelTooltipAbove: false,

    // Name/prompts panel state
    evalName: '',
    prompts: [] as EvalPrompt[],
    nextPromptId: 1,
    promptTransitionMs: 200,
    showPromptActions: false,
    activeImagePromptId: 0 as number,
    promptUploadError: '',

    // Settings panel state
    imagesPerModel: '1',
    isPublic: true,

    // Edit flow: set from #flow-data's edit_parent in init() when the create
    // flow was reopened to edit an existing dataset. null in the create flow.
    editParent: null as EditParent | null,

    // Terms/Privacy assent — must be checked before generation can be paid for.
    agreedToTerms: false,

    // ─── Evaluation step (Step 4) ─────────────────────────────────────────
    // 'pairwise' (head-to-head Elo) is the product default for a NEW run;
    // 'survey' (checklist) and 'none' (images only) are the other cards. A clone
    // seeds evalType (+ the type's fields) from the source eval.
    evalType: 'pairwise',
    judge: 'human',
    questionPreset: 'default',
    customQuestion: '',
    // Comparisons is a 15/30 preset dropdown plus a "custom" free-integer entry.
    comparisonsChoice: '30',
    customComparisons: '',
    // Survey editor. globalQuestions apply to every prompt. promptQuestions is
    // keyed by the STABLE prompt row id (EvalPrompt.id), NOT a positional index,
    // so adding/removing prompt rows never mis-attaches statements; the id→index
    // translation happens once, at serialise time (surveyQuestionsJson).
    globalQuestions: [''] as string[],
    promptQuestions: {} as Record<string, string[]>,
    promptQuestionsOpen: false,
    // Responses per image: 3/5 preset dropdown plus a "custom" free-integer entry.
    responsesChoice: '5',
    customResponses: '',
    surveyPresets: [] as SurveyPreset[],
    surveyLimits: {
        max_per_slate: 10,
        max_length: 300,
        responses_min: 1,
        responses_max: 50,
        responses_default: 5,
    } as SurveyLimits,

    // Server-computed pricing (single source of truth — must match what
    // Stripe will charge). Refreshed, debounced, whenever inputs change.
    serverPrice: null as RunPrice | null,
    priceFetchTimer: 0,
    priceFetchSeq: 0,

    init(): void {
        const dataEl = document.getElementById('flow-data');
        if (!dataEl) return;
        const flowData = JSON.parse(dataEl.textContent || '{}') as FlowData;
        this.customModels = flowData.models || [];
        this.modelGroups = flowData.groups || {};
        this.editParent = flowData.edit_parent || null;
        this.surveyPresets = flowData.survey_presets || [];
        if (flowData.survey_limits) this.surveyLimits = flowData.survey_limits;

        if (flowData.initial_state) {
            this.applyInitialState(flowData.initial_state);
        }

        // Default seed: the picker starts with a valid, visible selection
        // rather than an empty state.
        if (this.selectedCustomModelIds.length === 0) {
            this.selectModelGroup('recommended');
        }

        // Any input that affects price schedules a refresh. Prompt/model
        // mutations always replace the arrays, so plain $watch sees them; the
        // eval-side inputs (type/judge/comparisons/responses) are watched too.
        // Survey statement edits schedule from their own setters.
        const watched = [
            'prompts', 'selectedCustomModelIds', 'imagesPerModel',
            'evalType', 'judge', 'comparisonsChoice', 'customComparisons',
            'responsesChoice', 'customResponses',
        ];
        for (const prop of watched) {
            this.$watch(prop, () => this.schedulePriceRefresh());
        }
        this.schedulePriceRefresh();
    },

    schedulePriceRefresh(): void {
        window.clearTimeout(this.priceFetchTimer);
        this.priceFetchTimer = window.setTimeout(() => this.refreshPrice(), 350);
    },
    async refreshPrice(): Promise<void> {
        const seq = ++this.priceFetchSeq;
        if (this.promptCount() === 0 || this.selectedCustomModelIds.length < 2) {
            this.serverPrice = null;
            return;
        }
        // A survey with no statements yet can't be priced (the server returns
        // null anyway) — clear the whole summary until a statement exists.
        if (this.evalType === 'survey' && !this.hasAnyQuestion()) {
            this.serverPrice = null;
            return;
        }
        try {
            const price = await this.fetchRunPrice();
            if (seq === this.priceFetchSeq) {
                this.serverPrice = price;
            }
        } catch (err) {
            // Keep the last known price rather than flashing wrong numbers,
            // but log the failure so a broken preview isn't silently invisible.
            console.error('Price preview fetch failed:', err);
        }
    },
    // Unified generation + evaluation price. POST the whole run state as JSON:
    // the reuse split (clone) depends on exact prompt text + reference images,
    // and the survey blob needs the questions — so a query string won't do. The
    // server owns every figure (single source of truth == the Stripe charge).
    async fetchRunPrice(): Promise<RunPrice | null> {
        const prompts = this.surveyPromptRows().map((p) => ({
            text: p.value.trim(),
            image_s3_key: p.imageS3Key || null,
        }));
        const body: Record<string, unknown> = {
            prompts,
            models: [...this.selectedCustomModelIds],
            images_per_model: Number(this.imagesPerModel) || 1,
            eval_type: this.evalType,
            judge: this.judge,
            comparisons_per_model: this.comparisonsPerModel() || '30',
            survey_questions_json: this.surveyQuestionsJson(),
            responses_per_image: this.resolvedResponses(),
        };
        // A clone prices only newly generated cells against the parent's images.
        if (this.editParent) body.parent = this.editParent.slug;
        const csrf =
            (document.querySelector('[name=csrfmiddlewaretoken]') as HTMLInputElement | null)
                ?.value || '';
        const res = await fetch('/_run-price-preview/', {
            method: 'POST',
            body: JSON.stringify(body),
            headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrf },
        });
        if (!res.ok) throw new Error(`Price fetch failed (${res.status})`);
        const data = await res.json();
        return data.price;
    },
    applyInitialState(init: FlowInitialState): void {
        if (init.selectedModelIds && init.selectedModelIds.length > 0) {
            this.selectedCustomModelIds = [...init.selectedModelIds];
            this.pendingCustomModelIds = [...init.selectedModelIds];
            this.appliedModelGroup = this.detectModelGroup(init.selectedModelIds);
        } else if (init.appliedModelGroup) {
            const ids = this.modelGroups[init.appliedModelGroup];
            if (ids) {
                this.selectedCustomModelIds = [...ids];
                this.pendingCustomModelIds = [...ids];
                this.appliedModelGroup = init.appliedModelGroup;
            }
        }

        if (init.evalName) {
            this.evalName = init.evalName;
        }
        if (typeof init.isPublic === 'boolean') {
            this.isPublic = init.isPublic;
        }
        if (init.prompts && init.prompts.length > 0) {
            this.prompts = init.prompts.map((entry) => this.initialPromptRow(entry));
            this.showPromptActions = true;
            this.hasPromptContent = this.prompts.some((p) => p.value.length > 0);
            // Seeded reference images can make some models ineligible (img2img);
            // prune the selection the same way an interactive attach would.
            if (this.hasI2IPrompts()) {
                this.reconcileSelectionWithEligibility();
            }
        }
        if (init.imagesPerModel) {
            this.imagesPerModel = init.imagesPerModel;
        }
        if (init.activeStep) {
            this.activeStep = init.activeStep;
        }
        this.applyEvalInitialState(init);
    },
    // Seed the Evaluation step from a clone. Runs after the prompt rows are in
    // place so the survey blob's positional keys can map onto stable prompt ids.
    applyEvalInitialState(init: FlowInitialState): void {
        if (init.evalType === 'pairwise' || init.evalType === 'survey' || init.evalType === 'none') {
            this.evalType = init.evalType;
        }
        if (init.judge) {
            this.judge = init.judge;
        }
        if (init.questionPreset) {
            this.questionPreset = init.questionPreset;
        }
        if (init.questionCustom !== undefined) {
            this.customQuestion = init.questionCustom;
        }
        if (init.comparisonsPerModel !== undefined) {
            const cpm = String(init.comparisonsPerModel);
            if (cpm === '15' || cpm === '30') {
                this.comparisonsChoice = cpm;
            } else {
                this.comparisonsChoice = 'custom';
                this.customComparisons = cpm;
            }
        }
        if (init.responsesPerImage !== undefined) {
            const rpi = String(init.responsesPerImage);
            if (rpi === '3' || rpi === '5') {
                this.responsesChoice = rpi;
            } else {
                this.responsesChoice = 'custom';
                this.customResponses = rpi;
            }
        }
        if (init.surveyQuestionsJson) {
            this.seedSurveyQuestions(init.surveyQuestionsJson);
        }
    },
    // Rebuild the survey editors from the flat-text wire blob (surveyQuestionsJson
    // shape: {global: [...], per_prompt: {"<pos>": [...]}}). per_prompt keys are
    // POSITIONAL indices into the non-blank prompt order — map each onto that
    // prompt row's stable id so later add/remove of rows can't shift statements.
    seedSurveyQuestions(json: string): void {
        let parsed: { global?: string[]; per_prompt?: Record<string, string[]> };
        try {
            parsed = JSON.parse(json);
        } catch {
            return;
        }
        if (Array.isArray(parsed.global) && parsed.global.length > 0) {
            this.globalQuestions = parsed.global.slice();
        }
        const ordered = this.surveyPromptRows();
        const pq: Record<string, string[]> = {};
        for (const key of Object.keys(parsed.per_prompt || {})) {
            const pos = Number(key);
            if (Number.isInteger(pos) && pos >= 0 && pos < ordered.length) {
                pq[String(ordered[pos].id)] = (parsed.per_prompt?.[key] || []).slice();
            }
        }
        this.promptQuestions = pq;
    },
    // Build a prompt row from a create seed (plain string) or an edit seed
    // (object carrying the attached reference image + question).
    initialPromptRow(entry: string | InitialPrompt): EvalPrompt {
        if (typeof entry === 'string') {
            return { id: this.nextPromptId++, value: entry, isVisible: true };
        }
        const imageS3Key = entry.imageS3Key ?? undefined;
        return {
            id: this.nextPromptId++,
            value: entry.text,
            isVisible: true,
            imageS3Key,
            imageDataUrl: entry.imageUrl ?? undefined,
            imageName: imageS3Key ? imageS3Key.split('/').pop() || undefined : undefined,
            question: entry.question ?? undefined,
        };
    },

    // Step methods
    setActiveStep(step: number): void {
        this.activeStep = step;
    },
    nextStep(): void {
        this.activeStep += 1;
    },

    // Models methods
    selectModelGroup(groupKey: string): void {
        const group = this.modelGroups[groupKey];
        if (!group) return;

        this.selectedCustomModelIds = this.filterEligibleIds(group);
        this.pendingCustomModelIds = [...this.selectedCustomModelIds];
        this.appliedModelGroup = groupKey;
    },
    modelById(modelId: string): CustomModel | undefined {
        return this.customModels.find((m) => m.id === modelId);
    },
    toggleGroupInfo(groupKey: string): void {
        this.openGroupInfoKey = this.openGroupInfoKey === groupKey ? '' : groupKey;
    },
    isGroupInfoOpen(groupKey: string): boolean {
        return this.openGroupInfoKey === groupKey;
    },
    closeGroupInfo(groupKey: string): void {
        if (this.openGroupInfoKey === groupKey) {
            this.openGroupInfoKey = '';
        }
    },
    toggleModelInfo(modelId: string): void {
        this.openModelInfoId = this.openModelInfoId === modelId ? '' : modelId;
    },
    isModelInfoOpen(modelId: string): boolean {
        return this.openModelInfoId === modelId;
    },
    showModelTooltip(modelId: string, evt: Event): void {
        const model = this.modelById(modelId);
        if (!model || !model.blurb) return;
        const target = evt.currentTarget as HTMLElement | null;
        if (!target) return;
        const rect = target.getBoundingClientRect();
        const MAX_W = 300;
        const MARGIN = 8;
        this.modelTooltipText = model.blurb;
        this.modelTooltipLeft = Math.max(
            MARGIN,
            Math.min(rect.left, window.innerWidth - MAX_W - MARGIN),
        );
        // Prefer above the icon; drop below when there isn't room up top.
        this.modelTooltipAbove = rect.top > 160;
        this.modelTooltipTop = this.modelTooltipAbove ? rect.top - MARGIN : rect.bottom + MARGIN;
        this.modelTooltipVisible = true;
    },
    hideModelTooltip(): void {
        this.modelTooltipVisible = false;
    },
    modelTooltipStyle(): string {
        const transform = this.modelTooltipAbove ? 'translateY(-100%)' : 'none';
        return `left:${this.modelTooltipLeft}px;top:${this.modelTooltipTop}px;transform:${transform};max-width:300px;`;
    },
    selectedModelObjects(): CustomModel[] {
        const objects = this.selectedCustomModelIds
            .map((id) => this.modelById(id))
            .filter((m): m is CustomModel => !!m);
        // Cheap → pricey, so the chips read as a price ladder.
        return objects.sort((a, b) => a.cost_usd - b.cost_usd);
    },
    chipText(model: CustomModel): string {
        return `${model.name} · ${model.cost}`;
    },
    removeModelLabel(model: CustomModel): string {
        return `Remove ${model.name}`;
    },
    selectionSummary(): string {
        const n = this.selectedCustomModelIds.length;
        if (n === 0) return 'No models selected — pick a preset or search below';
        const total = this.selectedModelObjects().reduce((sum, m) => sum + (m.cost_usd || 0), 0);
        const totalDisplay = total < 0.01 ? `$${total.toFixed(3)}` : `$${total.toFixed(2)}`;
        return `${n} model${n === 1 ? '' : 's'} · ~${totalDisplay} in images per prompt`;
    },
    addModel(modelId: string): void {
        if (this.selectedCustomModelIds.includes(modelId)) return;
        if (!this.isModelEligible(modelId)) return;
        this.selectedCustomModelIds = [...this.selectedCustomModelIds, modelId];
        this.pendingCustomModelIds = [...this.selectedCustomModelIds];
        this.appliedModelGroup = this.detectModelGroup(this.selectedCustomModelIds);
    },
    removeModel(modelId: string): void {
        this.selectedCustomModelIds = this.selectedCustomModelIds.filter((id) => id !== modelId);
        this.pendingCustomModelIds = [...this.selectedCustomModelIds];
        this.appliedModelGroup = this.selectedCustomModelIds.length
            ? this.detectModelGroup(this.selectedCustomModelIds)
            : '';
    },
    clearAllModels(): void {
        this.selectedCustomModelIds = [];
        this.pendingCustomModelIds = [];
        this.appliedModelGroup = '';
    },
    hasSelectedModels(): boolean {
        return this.selectedCustomModelIds.length > 0;
    },

    // ─── Search-to-add ───────────────────────────────────────────────────
    modelSearchResults(): CustomModel[] {
        const query = normalizeModelSearch(this.modelSearchQuery);
        if (!query) return [];
        // Already-selected models stay in the results (marked as added) —
        // hiding them makes a hit on a selected model look like no match.
        return this.eligibleCustomModels()
            .filter((m) => normalizeModelSearch(`${m.name} ${m.publisher} ${m.id}`).includes(query))
            .slice(0, 8);
    },
    isModelSelected(modelId: string): boolean {
        return this.selectedCustomModelIds.includes(modelId);
    },
    toggleModelFromSearch(modelId: string): void {
        if (this.isModelSelected(modelId)) {
            this.removeModel(modelId);
        } else {
            this.addModelFromSearch(modelId);
        }
    },
    searchResultRowClass(modelId: string): string {
        if (this.isModelSelected(modelId)) {
            return 'opacity-60';
        }
        return '';
    },
    searchDropdownVisible(): boolean {
        return this.modelSearchOpen && normalizeModelSearch(this.modelSearchQuery).length > 0;
    },
    hasNoSearchResults(): boolean {
        return this.modelSearchResults().length === 0;
    },
    searchResultMeta(model: CustomModel): string {
        const meta = `${model.publisher} · ${model.cost}`;
        return this.isModelSelected(model.id) ? `Added ✓ · ${meta}` : meta;
    },
    openModelSearch(): void {
        this.modelSearchOpen = true;
    },
    closeModelSearch(): void {
        this.modelSearchOpen = false;
    },
    addModelFromSearch(modelId: string): void {
        this.addModel(modelId);
        this.modelSearchQuery = '';
    },
    addFirstSearchResult(): void {
        // Enter adds the first result that isn't already selected — it must
        // never silently remove one.
        const first = this.modelSearchResults().find((m) => !this.isModelSelected(m.id));
        if (first) this.addModelFromSearch(first.id);
    },

    // ─── Browse-all modal ────────────────────────────────────────────────
    openCustomModels(): void {
        this.pendingCustomModelIds = [...this.selectedCustomModelIds];
        this.browseQuery = '';
        this.browseOpenWeightsOnly = false;
        this.browseSortRules = [];
        this.customModelsOpen = true;
    },
    closeCustomModels(): void {
        this.customModelsOpen = false;
        this.pendingCustomModelIds = [...this.selectedCustomModelIds];
    },
    browseFilteredModels(): CustomModel[] {
        const query = normalizeModelSearch(this.browseQuery);
        const rows = this.eligibleCustomModels().filter((m) => {
            if (this.browseOpenWeightsOnly && !m.open_weights) return false;
            if (!query) return true;
            return normalizeModelSearch(`${m.name} ${m.publisher} ${m.id}`).includes(query);
        });

        const rules = this.browseSortRules as BrowseSortRule[];
        if (rules.length === 0) return rows;
        return rows.sort((a, b) => {
            for (const rule of rules) {
                const order = compareBrowseModels(a, b, rule);
                if (order !== 0) return order;
            }
            // Ties keep catalog order — Array#sort is stable.
            return 0;
        });
    },
    toggleBrowseOpenWeights(): void {
        this.browseOpenWeightsOnly = !this.browseOpenWeightsOnly;
    },

    // ─── Browse-modal column sort ────────────────────────────────────────
    toggleBrowseSort(key: BrowseSortKey): void {
        const rules = this.browseSortRules as BrowseSortRule[];
        const [primary, ...rest] = rules;
        if (primary && primary.key === key) {
            // Re-clicking the primary column flips it; its tiebreakers stay put.
            const dir = primary.dir === 'asc' ? 'desc' : 'asc';
            this.browseSortRules = [{ key, dir }, ...rest];
            return;
        }

        // A new column becomes primary and demotes the previous rules to
        // tiebreakers, so ties in it are broken by the sort before it.
        const kept = rules.filter((r) => r.key !== key).slice(0, BROWSE_SORT_DEPTH - 1);
        this.browseSortRules = [{ key, dir: BROWSE_SORT_DEFAULT_DIR[key] }, ...kept];
    },
    browseSortRuleFor(key: BrowseSortKey): BrowseSortRule | undefined {
        return (this.browseSortRules as BrowseSortRule[]).find((r) => r.key === key);
    },
    browseSortIndicator(key: BrowseSortKey): string {
        const rule = this.browseSortRuleFor(key);
        if (!rule) return '';
        return rule.dir === 'asc' ? '↑' : '↓';
    },
    browseSortIndicatorClass(key: BrowseSortKey): string {
        const rules = this.browseSortRules as BrowseSortRule[];
        if (rules.length && rules[0].key === key) return 'text-foreground';
        // Tiebreaker columns keep a dimmed arrow so the sort stack is visible.
        return 'text-foreground-muted/60';
    },
    browseSortAria(key: BrowseSortKey): string {
        const rule = this.browseSortRuleFor(key);
        if (!rule) return 'none';
        return rule.dir === 'asc' ? 'ascending' : 'descending';
    },
    hasNoBrowseResults(): boolean {
        return this.browseFilteredModels().length === 0;
    },
    selectAllShown(): void {
        const shown = this.browseFilteredModels().map((m) => m.id);
        this.pendingCustomModelIds = [...new Set([...this.pendingCustomModelIds, ...shown])];
    },
    selectAllShownLabel(): string {
        const n = this.browseFilteredModels().length;
        return `Select all ${n} shown`;
    },
    openWeightsDisplay(model: CustomModel): string {
        return model.open_weights ? 'Yes' : '—';
    },
    detectModelGroup(modelIds: string[]): string {
        // Compare against each group's *eligible* (img2img-filtered) IDs so that
        // e.g. "all i2i-capable Frontier models" still detects as 'frontier' in
        // img2img mode.
        const selected = new Set(modelIds);
        const groups = this.modelGroups as Record<string, string[]>;
        for (const key of Object.keys(groups)) {
            const ids = this.filterEligibleIds(groups[key]);
            if (ids.length !== selected.size) continue;
            if (ids.every((id) => selected.has(id))) {
                return key;
            }
        }
        return 'custom';
    },

    // ─── Img2img awareness ───────────────────────────────────────────────
    hasI2IPrompts(): boolean {
        return this.prompts.some(
            (p) => p.isVisible && (p.imageS3Key || p.imageDataUrl),
        );
    },
    isModelEligible(modelId: string): boolean {
        if (!this.hasI2IPrompts()) return true;
        const m = this.customModels.find((cm) => cm.id === modelId);
        return !!(m && m.supports_image_input);
    },
    filterEligibleIds(ids: string[]): string[] {
        if (!this.hasI2IPrompts()) return [...ids];
        return ids.filter((id) => this.isModelEligible(id));
    },
    eligibleCustomModels(): CustomModel[] {
        // Drop same-model provider duplicates so browse/search show one row per
        // real model. Chips read customModels directly, so a model an existing
        // eval already selected still resolves even if it's hidden here.
        const base = this.customModels.filter((m) => !m.redundant);
        if (!this.hasI2IPrompts()) return base;
        return base.filter((m) => m.supports_image_input);
    },
    eligibleCatalogCount(): number {
        return this.eligibleCustomModels().length;
    },
    groupSize(groupKey: string): number {
        const ids = this.modelGroups[groupKey];
        if (!ids) return 0;
        return this.filterEligibleIds(ids).length;
    },
    groupAvgCostDisplay(groupKey: string): string {
        const ids = this.filterEligibleIds(this.modelGroups[groupKey] || []);
        if (ids.length === 0) return '$0.00';
        const lookup = new Set(ids);
        const costs = this.customModels
            .filter((m) => lookup.has(m.id))
            .map((m) => m.cost_usd || 0);
        const avg = costs.reduce((a, b) => a + b, 0) / costs.length;
        return avg < 0.01 ? `$${avg.toFixed(3)}` : `$${avg.toFixed(2)}`;
    },
    groupSubtitle(groupKey: string): string {
        const n = this.groupSize(groupKey);
        return `${n} model${n === 1 ? '' : 's'} · ~${this.groupAvgCostDisplay(groupKey)}/image`;
    },
    isGroupDisabled(groupKey: string): boolean {
        // A preset needs at least 2 eligible models to be a valid selection.
        return this.groupSize(groupKey) < 2;
    },
    /** Re-apply the currently-selected preset (if any) and prune custom selections
     *  to eligible-only models. Call this whenever img2img state changes. */
    reconcileSelectionWithEligibility(): void {
        const group = this.modelGroups[this.appliedModelGroup];
        if (group) {
            // Re-apply the seed so its models come back when eligibility relaxes
            // (e.g. the user removes the reference image again).
            this.selectedCustomModelIds = this.filterEligibleIds(group);
        } else {
            this.selectedCustomModelIds = this.filterEligibleIds(this.selectedCustomModelIds);
        }
        this.pendingCustomModelIds = [...this.selectedCustomModelIds];
        this.appliedModelGroup = this.selectedCustomModelIds.length
            ? this.detectModelGroup(this.selectedCustomModelIds)
            : '';
    },
    applyCustomModels(): void {
        if (!this.canApplyCustomModels()) {
            return;
        }

        this.selectedCustomModelIds = [...this.pendingCustomModelIds];
        this.appliedModelGroup = this.detectModelGroup(this.pendingCustomModelIds);
        this.customModelsOpen = false;
    },
    toggleCustomModel(modelId: string): void {
        if (this.pendingCustomModelIds.includes(modelId)) {
            this.pendingCustomModelIds = this.pendingCustomModelIds.filter((id) => id !== modelId);
            return;
        }

        this.pendingCustomModelIds = [...this.pendingCustomModelIds, modelId];
    },
    isCustomModelPending(modelId: string): boolean {
        return this.pendingCustomModelIds.includes(modelId);
    },
    customModelMeta(model: { publisher: string; elo: string; released: string; cost: string }): string {
        return `${model.publisher} · Elo ${model.elo} · ${model.released} · ${model.cost}`;
    },
    customModelRowClass(modelId: string): string {
        if (this.isCustomModelPending(modelId)) {
            return 'border-theme-primary bg-surface';
        }

        return 'border-border bg-surface-subtle hover:border-foreground-muted/50 focus-within:border-foreground-muted/50';
    },
    customModelDesktopRowClass(modelId: string): string {
        if (this.isCustomModelPending(modelId)) {
            return 'bg-surface-strong focus-within:bg-surface-strong-hover dark:bg-theme-primary/10 dark:focus-within:bg-theme-primary/30';
        }

        return 'hover:bg-surface-strong/60 focus-within:bg-surface-strong/80 dark:focus-within:bg-surface-subtle';
    },
    customModelIndicatorClass(modelId: string): string {
        if (this.isCustomModelPending(modelId)) {
            return 'border-theme-primary bg-theme-primary';
        }

        return 'border-border bg-transparent group-focus-within:border-foreground-muted/50 dark:group-focus-within:border-theme-primary';
    },
    handleCustomModelsEscape(): void {
        if (!this.customModelsOpen) {
            return;
        }

        this.closeCustomModels();
    },

    // Name/prompts methods
    addPrompt(): void {
        const isFirstPrompt = !this.hasPromptRows();
        const promptId = this.nextPromptId++;

        this.prompts = [...this.prompts, { id: promptId, value: '', isVisible: false }];

        Alpine.nextTick(() => {
            this.setPromptVisibility(promptId, true);

            if (isFirstPrompt) {
                this.showPromptActions = true;
            }

            this.focusPromptInput(promptId);
        });
    },
    removePrompt(id: number): void {
        if (this.promptCount() === 1) {
            this.showPromptActions = false;
        }

        this.setPromptVisibility(id, false);

        window.setTimeout(() => {
            this.prompts = this.prompts.filter((prompt) => prompt.id !== id);
        }, this.promptTransitionMs);
    },
    updatePrompt(id: number, value: string): void {
        this.updatePromptById(id, (prompt) => ({ ...prompt, value }));
    },
    setPromptVisibility(id: number, isVisible: boolean): void {
        this.updatePromptById(id, (prompt) => ({ ...prompt, isVisible }));
    },
    updatePromptById(id: number, updater: (prompt: EvalPrompt) => EvalPrompt): void {
        this.prompts = this.prompts.map((prompt) => (prompt.id === id ? updater(prompt) : prompt));
    },
    focusPromptInput(id: number): void {
        window.setTimeout(() => {
            document.querySelector<HTMLTextAreaElement>(`[data-prompt-input-id="${id}"]`)?.focus();
        }, this.promptTransitionMs);
    },
    autosizePromptInput(el: HTMLTextAreaElement, _value?: string): void {
        if (!el.isConnected) {
            return;
        }
        if (el.scrollHeight === 0) {
            // Hidden (mid enter-transition) — measure once it's rendered
            window.requestAnimationFrame(() => this.autosizePromptInput(el));
            return;
        }
        el.style.height = 'auto';
        // scrollHeight excludes borders; offsetHeight - clientHeight adds them back
        el.style.height = `${el.scrollHeight + el.offsetHeight - el.clientHeight}px`;
    },
    promptCount(): number {
        return this.prompts.filter((prompt) => prompt.isVisible).length;
    },
    // Structural: any visible prompt row exists. Drives the empty-state vs
    // rows toggle so a freshly-added (still-blank) row renders immediately.
    hasPromptRows(): boolean {
        return this.prompts.some((prompt) => prompt.isVisible);
    },
    // Content: at least one visible prompt has non-blank text. Gates the pay
    // button and step 1's Continue — a single empty row must not enable submit
    // (the server rejects it and the reload wipes all Alpine state).
    hasPrompts(): boolean {
        return this.prompts.some((prompt) => prompt.isVisible && prompt.value.trim().length > 0);
    },
    triggerPromptFileUpload(refName: string): void {
        const input = (this.$refs as Record<string, HTMLInputElement>)[refName];
        if (input) input.click();
    },
    openImagePickerForPrompt(promptId: number): void {
        this.activeImagePromptId = promptId;
        this.triggerPromptFileUpload('imageFileInput');
    },
    handleImageInputChange(event: Event): void {
        const target = event.target as HTMLInputElement;
        const file = target.files && target.files[0];
        target.value = '';
        if (this.activeImagePromptId && file) {
            this.attachImageToPrompt(this.activeImagePromptId, file);
        }
        this.activeImagePromptId = 0;
    },
    async loadPromptsFromFileEvent(event: Event): Promise<void> {
        const target = event.target as HTMLInputElement;
        const files = Array.from(target.files || []);
        target.value = '';
        await this.loadPromptsFromFiles(files);
    },
    async loadPromptsFromFiles(files: File[]): Promise<void> {
        if (!files.length) return;
        this.promptUploadError = '';
        const byExt = (ext: string) => files.find((f) => f.name.toLowerCase().endsWith(ext));
        const csv = byExt('.csv');
        const zip = byExt('.zip');
        if (csv) {
            await this.importPromptsCsv(csv, zip);
            return;
        }
        if (zip) {
            this.promptUploadError =
                'Select the CSV together with its ZIP (pick both files in the dialog).';
            return;
        }
        for (const file of files) {
            await this.loadPromptsFromFile(file);
        }
    },
    async loadPromptsFromFile(file: File | null | undefined): Promise<void> {
        if (!file) return;
        const text = await file.text();
        const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
        if (lines.length === 0) return;

        const isFirst = !this.hasPromptRows();
        const newPrompts: EvalPrompt[] = lines.map((value) => ({
            id: this.nextPromptId++,
            value,
            isVisible: true,
        }));
        this.prompts = [...this.prompts, ...newPrompts];
        if (isFirst) this.showPromptActions = true;
        this.hasPromptContent = this.prompts.some((p) => p.value.length > 0);
    },
    async importPromptsCsv(csv: File, zip?: File): Promise<void> {
        // Server-side parse: extracts zip images, uploads them to S3, and
        // returns the parsed rows (incl. per-prompt questions) as JSON.
        const formData = new FormData();
        formData.append('prompt_csv', csv);
        if (zip) formData.append('prompt_zip', zip);
        const csrf = (document.querySelector('[name=csrfmiddlewaretoken]') as HTMLInputElement | null)?.value || '';
        try {
            const res = await fetch('/_upload-prompts/?format=json', {
                method: 'POST',
                body: formData,
                headers: { 'X-CSRFToken': csrf },
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                throw new Error(data.error || `Upload failed (${res.status})`);
            }
            const isFirst = !this.hasPromptRows();
            const rows: EvalPrompt[] = ((data.prompts || []) as UploadedPrompt[]).map((p) => ({
                id: this.nextPromptId++,
                value: p.text,
                isVisible: true,
                imageS3Key: p.image_s3_key || undefined,
                imageName: p.image_name || undefined,
                imageDataUrl: p.image_url || undefined,
                question: p.question || undefined,
            }));
            this.prompts = [...this.prompts, ...rows];
            if (isFirst && rows.length) this.showPromptActions = true;
            this.hasPromptContent = this.prompts.some((p) => p.value.length > 0);
            this.reconcileSelectionWithEligibility();
        } catch (e) {
            this.promptUploadError = e instanceof Error ? e.message : 'Upload failed';
        }
    },
    async attachImageToPrompt(promptId: number, file: File | null | undefined): Promise<void> {
        if (!file) return;
        const dataUrl = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = () => reject(reader.error);
            reader.readAsDataURL(file);
        });
        this.updatePromptById(promptId, (p) => ({
            ...p,
            imageDataUrl: dataUrl,
            imageName: file.name,
            imageS3Key: undefined,
            imageUploading: true,
            imageUploadError: undefined,
        }));

        const formData = new FormData();
        formData.append('image', file);
        const csrf = (document.querySelector('[name=csrfmiddlewaretoken]') as HTMLInputElement | null)?.value || '';
        try {
            const res = await fetch('/_upload-prompt-image/', {
                method: 'POST',
                body: formData,
                headers: { 'X-CSRFToken': csrf },
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({ error: `Upload failed (${res.status})` }));
                throw new Error(err.error || `Upload failed (${res.status})`);
            }
            const data = await res.json();
            this.updatePromptById(promptId, (p) => ({
                ...p,
                imageS3Key: data.s3_key,
                imageUploading: false,
            }));
            this.reconcileSelectionWithEligibility();
        } catch (e) {
            this.updatePromptById(promptId, (p) => ({
                ...p,
                imageUploading: false,
                imageUploadError: e instanceof Error ? e.message : 'Upload failed',
            }));
        }
    },
    promptTextFieldName(idx: number): string {
        return `prompt_text_${idx}`;
    },
    promptImageS3KeyFieldName(idx: number): string {
        return `prompt_image_s3_key_${idx}`;
    },
    promptQuestionFieldName(idx: number): string {
        return `prompt_question_${idx}`;
    },
    hasUploadInFlight(): boolean {
        return this.prompts.some((p) => p.imageUploading);
    },
    clearPromptImage(promptId: number): void {
        this.updatePromptById(promptId, (p) => ({
            ...p,
            imageDataUrl: undefined,
            imageName: undefined,
            imageS3Key: undefined,
            imageUploading: false,
            imageUploadError: undefined,
        }));
        this.reconcileSelectionWithEligibility();
    },

    // Validation methods
    pendingCustomModelCount(): number {
        return this.pendingCustomModelIds.length;
    },
    selectedCustomModelCount(): number {
        return this.selectedCustomModelIds.length;
    },
    browseAllLabel(): string {
        const n = this.eligibleCatalogCount();
        const suffix = this.hasI2IPrompts() ? ' image-capable' : '';
        return `Browse all ${n}${suffix} models`;
    },
    canApplyCustomModels(): boolean {
        return this.pendingCustomModelCount() >= 2;
    },
    canContinueModelsStep(): boolean {
        return this.selectedCustomModelIds.length >= 2;
    },
    isModelGroupSelected(groupKey: string): boolean {
        return this.appliedModelGroup === groupKey;
    },

    // ─── Evaluation step (Step 4) ─────────────────────────────────────────
    selectEvalType(type: string): void {
        this.evalType = type;
    },
    // Effective comparisons-per-model — the hidden field's value and the price
    // input. Empty while 'custom' is selected but not yet filled.
    comparisonsPerModel(): string {
        return this.comparisonsChoice === 'custom' ? this.customComparisons : this.comparisonsChoice;
    },
    // Custom question requires text; a preset is always valid. Pairwise only.
    questionValid(): boolean {
        return this.questionPreset !== 'custom' || this.customQuestion.trim().length > 0;
    },
    togglePromptQuestions(): void {
        this.promptQuestionsOpen = !this.promptQuestionsOpen;
    },
    // Effective responses-per-image (survey). Empty while 'custom' is unfilled.
    resolvedResponses(): string {
        return this.responsesChoice === 'custom' ? this.customResponses : this.responsesChoice;
    },
    resolvedResponsesValid(): boolean {
        const v = this.resolvedResponses();
        if (v.length === 0) return false;
        const n = Number(v);
        return (
            Number.isInteger(n) &&
            n >= this.surveyLimits.responses_min &&
            n <= this.surveyLimits.responses_max
        );
    },

    // ── Survey statement editors ──────────────────────────────────────────
    // The CSP Alpine build forbids property assignment in templates, so array
    // items are read one-way (:value) and written back through these setters.
    // The prompts that will be posted + rated: visible rows with non-blank text,
    // in order. A prompt's POSITIONAL index in THIS list is what the backend and
    // price preview key per-prompt statements on (surveyQuestionsJson emits it).
    surveyPromptRows(): EvalPrompt[] {
        return this.prompts.filter((p) => p.isVisible && p.value.trim().length > 0);
    },
    surveyPromptCount(): number {
        return this.surveyPromptRows().length;
    },
    setGlobalQuestion(i: number, value: string): void {
        this.globalQuestions[i] = value;
        this.schedulePriceRefresh();
    },
    addGlobalQuestion(): void {
        this.globalQuestions.push('');
        this.$nextTick(() => {
            const inputs = document.querySelectorAll<HTMLInputElement>('[data-global-q]');
            inputs[inputs.length - 1]?.focus();
        });
    },
    removeGlobalQuestion(i: number): void {
        this.globalQuestions.splice(i, 1);
        // Keep at least one row so the editor never collapses to nothing.
        if (this.globalQuestions.length === 0) this.globalQuestions.push('');
        this.schedulePriceRefresh();
    },
    addPresetQuestion(question: string): void {
        const trimmed = question.trim();
        if (this.globalQuestions.some((q) => q.trim() === trimmed)) return;
        // Reuse a trailing empty row rather than leaving a blank gap above it.
        const last = this.globalQuestions.length - 1;
        if (last >= 0 && this.globalQuestions[last].trim() === '') {
            this.globalQuestions[last] = question;
        } else {
            this.globalQuestions.push(question);
        }
        this.schedulePriceRefresh();
    },
    promptQuestionRows(id: number): string[] {
        return this.promptQuestions[String(id)] || [];
    },
    // Per-prompt statements are keyed by a DYNAMIC prompt id, so mutate them by
    // reassigning the whole map (datasetFlow's idiom) — adding a brand-new key
    // that way is reliably reactive across Alpine builds.
    setPromptQuestion(id: number, i: number, value: string): void {
        const key = String(id);
        const rows = [...(this.promptQuestions[key] || [])];
        rows[i] = value;
        this.promptQuestions = { ...this.promptQuestions, [key]: rows };
        this.schedulePriceRefresh();
    },
    addPromptQuestion(id: number): void {
        const key = String(id);
        const rows = [...(this.promptQuestions[key] || []), ''];
        this.promptQuestions = { ...this.promptQuestions, [key]: rows };
        this.$nextTick(() => {
            const inputs = document.querySelectorAll<HTMLInputElement>(
                `[data-prompt-list="${id}"] input`,
            );
            inputs[inputs.length - 1]?.focus();
        });
    },
    removePromptQuestion(id: number, i: number): void {
        const key = String(id);
        const rows = [...(this.promptQuestions[key] || [])];
        rows.splice(i, 1);
        this.promptQuestions = { ...this.promptQuestions, [key]: rows };
        this.schedulePriceRefresh();
    },
    nonEmptyGlobalCount(): number {
        return this.globalQuestions.filter((q) => q.trim().length > 0).length;
    },
    promptSpecificCount(): number {
        let n = 0;
        for (const p of this.surveyPromptRows()) {
            n += this.promptQuestionRows(p.id).filter((q) => q.trim().length > 0).length;
        }
        return n;
    },
    // Coverage (D3), display-only: a global statement covers every prompt;
    // otherwise only prompts with their own statements are covered. Feeds the
    // "Only prompts with statements are rated (k of N)" note — priced counts
    // come from the server (summarize_survey_questions).
    coveredPromptCount(): number {
        const hasGlobal = this.nonEmptyGlobalCount() > 0;
        let k = 0;
        for (const p of this.surveyPromptRows()) {
            const specific = this.promptQuestionRows(p.id).filter((q) => q.trim().length > 0).length;
            if (hasGlobal || specific > 0) k += 1;
        }
        return k;
    },
    // Whether the survey has any non-empty statement — the submit/price gate
    // (the server owns the exact coverage/count math).
    hasAnyQuestion(): boolean {
        return this.nonEmptyGlobalCount() > 0 || this.promptSpecificCount() > 0;
    },
    // Serialise the survey editors to the flat-text wire blob. per_prompt is
    // keyed by each row's POSITIONAL index in surveyPromptRows() (what the
    // backend + preview parse), translated from the stable prompt id here.
    surveyQuestionsJson(): string {
        const global = this.globalQuestions.map((q) => q.trim()).filter((q) => q.length > 0);
        const perPrompt: Record<string, string[]> = {};
        this.surveyPromptRows().forEach((p, pos) => {
            const rows = this.promptQuestionRows(p.id)
                .map((q) => q.trim())
                .filter((q) => q.length > 0);
            if (rows.length > 0) perPrompt[String(pos)] = rows;
        });
        return JSON.stringify({ global, per_prompt: perPrompt });
    },
    surveyValid(): boolean {
        return this.hasAnyQuestion() && this.resolvedResponsesValid();
    },
    // The active eval type's own validity — gates the Evaluation-step Continue
    // and the final submit (alongside terms + prompts + >=2 models). 'none' has
    // no eval config to validate.
    evalValid(): boolean {
        if (this.evalType === 'survey') return this.surveyValid();
        if (this.evalType === 'pairwise') {
            return this.questionValid() && this.comparisonsPerModel().length > 0;
        }
        return true;
    },

    // ─── Summary display ──────────────────────────────────────────────────
    // All money figures come from the server's calculate_run_price() (via
    // refreshPrice) so they always match what Stripe will charge — including the
    // platform fee and the single $0.50 minimum on the combined total.
    totalImageCount(): number {
        if (this.serverPrice) return this.serverPrice.generation.num_images;
        return this.promptCount() * this.selectedCustomModelIds.length * (Number(this.imagesPerModel) || 0);
    },
    formatCents(cents: number): string {
        return `$${(cents / 100).toFixed(2)}`;
    },
    imageGenerationCostDisplay(): string {
        return this.serverPrice ? this.formatCents(this.serverPrice.image_cost_cents) : '—';
    },
    totalCostDisplay(): string {
        // total_cents can legitimately be 0 (a free zero-new clone with no eval),
        // so guard on the price object's presence, not its truthiness.
        return this.serverPrice ? this.formatCents(this.serverPrice.total_cents) : '—';
    },
    // The rating half is shown only when the price carries an evaluation
    // (pairwise/survey); images-only omits the whole line.
    evaluationActive(): boolean {
        return !!(this.serverPrice && this.serverPrice.evaluation);
    },
    evaluationLabel(): string {
        return this.serverPrice?.evaluation_label || 'Evaluation';
    },
    evaluationCountText(): string {
        const evaluation = this.serverPrice?.evaluation;
        if (!evaluation) return '';
        if (evaluation.num_responses !== undefined) return `${evaluation.num_responses} responses`;
        if (evaluation.num_comparisons !== undefined) return `${evaluation.num_comparisons} comparisons`;
        return '';
    },
    ratingCostDisplay(): string {
        return this.serverPrice ? this.formatCents(this.serverPrice.rating_cost_cents) : '—';
    },

    // ─── Clone flow ───────────────────────────────────────────────────────
    isEditMode(): boolean {
        return this.editParent !== null;
    },
    // "6 new images · 12 reused" — only in clone mode, once a price has loaded.
    reusedImagesDisplay(): string {
        if (!this.isEditMode() || !this.serverPrice) return '';
        return `${this.serverPrice.num_new_images} new images · ${this.serverPrice.num_reused_images} reused`;
    },
    // The parent's images have expired, so nothing can be copied — the whole
    // grid is regenerated and the note warns the user before they pay.
    showExpiredNote(): boolean {
        return this.isEditMode() && !!this.editParent && this.editParent.images_expired;
    },

    // Verb tracks whether raters run: 'generate' for images-only, 'launch' for a
    // pairwise/survey eval — same verbs for create and clone. A free clone
    // (total 0, no new images + no eval) just applies changes with no charge.
    payButtonLabel(): string {
        const price = this.serverPrice;
        if (!price) {
            return this.isEditMode() ? 'Apply changes' : 'Pay & generate';
        }
        if (this.isEditMode() && price.total_cents === 0) {
            return 'Apply changes';
        }
        const verb = this.evalType === 'none' ? 'Pay & generate' : 'Pay & launch';
        return `${verb} - ${this.formatCents(price.total_cents)}`;
    },
}));


// Eval Title Rename //////////////////////////////////////////////////////////////////////
// Owner-only inline rename beside the eval title (partials/_eval_title.html).
// Alpine owns nothing but the open/closed toggle — the save posts over HTMX and
// the server re-renders the island, so the displayed name always comes from the
// database rather than from a client-side copy of it.
Alpine.data('evalTitle', () => ({
    editing: false,

    init(): void {
        const root = document.getElementById('eval-title');
        if (!root) return;
        // Keep the browser tab in sync after a rename: the <title> lives in
        // <head>, where the swap can't reach. Runs on the first page load too,
        // where it's a no-op (same string the server already rendered).
        const pageTitle = root.dataset.pageTitle;
        if (pageTitle) document.title = pageTitle;
        // The server re-renders the island in edit mode when it rejects a name.
        if (root.dataset.editing) {
            this.editing = true;
            this.focusInput();
        }
    },

    // The CSP Alpine build has no expression evaluator, so `!editing` can't go
    // in the directive — same reason addComparisonsForm has isFormClosed().
    notEditing(): boolean {
        return !this.editing;
    },
    startEdit(): void {
        this.editing = true;
        this.focusInput();
    },
    cancelEdit(): void {
        this.editing = false;
    },
    focusInput(): void {
        this.$nextTick(() => {
            const input = document.getElementById('eval-title-input') as HTMLInputElement | null;
            if (!input) return;
            input.focus();
            // Whole name selected, so typing replaces it outright.
            input.select();
        });
    },
}));


// Header Action Menus ////////////////////////////////////////////////////////////////////
// The eval page's action row keeps only Copy link + the primary CTAs visible;
// secondary actions collapse into two dropdowns (progressive disclosure):
// a "Download" menu and a "⋯" overflow. CSP Alpine build has no expression
// evaluator, so every directive calls a method or reads a bare property.

// Generic open/close dropdown — used by the Download menu.
Alpine.data('dropdownMenu', () => ({
    open: false,

    toggle(): void {
        this.open = !this.open;
    },
    closeMenu(): void {
        this.open = false;
    },
}));

// Owner-only delete: a compact trash icon button + a type-to-confirm modal, so
// a permanent delete is hard to trigger by accident — the destructive submit
// stays disabled until the user types "delete". The server re-checks ownership
// and never trusts that this gate ran.
Alpine.data('deleteEvalDialog', () => ({
    dialogOpen: false,
    confirmText: '',
    // Locks the form after the first submission: a double-click (or
    // Enter-then-click) races two POSTs, and the browser renders whichever
    // navigation started last — which 404'd on prod once the first POST had
    // already deleted the row. The server is idempotent about it too; this
    // just stops the race at the source.
    submitting: false,

    openDialog(): void {
        this.confirmText = '';
        this.submitting = false;
        this.dialogOpen = true;
        this.$nextTick(() => {
            const input = document.getElementById(
                'delete-eval-confirm-input',
            ) as HTMLInputElement | null;
            input?.focus();
        });
    },
    closeDialog(): void {
        this.dialogOpen = false;
    },
    // The literal word "delete", case-insensitive and trimmed.
    confirmed(): boolean {
        return this.confirmText.trim().toLowerCase() === 'delete';
    },
    submitLocked(): boolean {
        return !this.confirmed() || this.submitting;
    },
    // @submit handler (the CSP build passes $event to a bare method reference).
    // Lets the first submission through and locks; belt-and-braces blocks any
    // second one that still fires (e.g. Enter while the button disables).
    onSubmit(event: Event): void {
        if (this.submitting) {
            event.preventDefault();
            return;
        }
        this.submitting = true;
    },
}));


// Add Comparisons Form ///////////////////////////////////////////////////////////////////
// Rendered on a RATING/COMPLETE eval's page. Buys more head-to-head comparisons
// on the existing eval (posts a NATIVE form to /e/<slug>/add-comparisons/). It
// only gates the submit button and drives the live price preview — it never
// computes price client-side (server price == Stripe charge).
Alpine.data('addComparisonsForm', () => ({
    slug: '',
    comparisons: '30',
    agreedToTerms: false,
    formOpen: false,

    serverPrice: null as EvalPrice | null,
    priceFetchTimer: 0,
    priceFetchSeq: 0,

    init(): void {
        // Server-provided config: the slug plus whether to start expanded (the
        // server opens the form on a validation reject). Read it from the
        // json_script tag the panel renders alongside this island.
        const cfgEl = document.getElementById('add-comparisons-config');
        if (cfgEl) {
            const cfg = JSON.parse(cfgEl.textContent || '{}') as {
                slug?: string;
                open?: boolean;
            };
            this.slug = cfg.slug || '';
            if (cfg.open) this.formOpen = true;
        }
        this.$watch('comparisons', () => this.schedulePriceRefresh());
        // Fetch the price lazily — only when the form starts (or is) open.
        // openForm() fetches on first expand; here covers the auto-open case.
        if (this.formOpen) this.refreshPrice();
    },

    isFormClosed(): boolean {
        return !this.formOpen;
    },
    openForm(): void {
        this.formOpen = true;
        if (this.serverPrice === null) this.refreshPrice();
        this.$nextTick(() => document.getElementById('add-comparisons-input')?.focus());
    },
    closeForm(): void {
        this.formOpen = false;
    },

    schedulePriceRefresh(): void {
        window.clearTimeout(this.priceFetchTimer);
        this.priceFetchTimer = window.setTimeout(() => this.refreshPrice(), 350);
    },
    async refreshPrice(): Promise<void> {
        const seq = ++this.priceFetchSeq;
        if (!this.slug || this.comparisons.length === 0) {
            this.serverPrice = null;
            return;
        }
        const params = new URLSearchParams({
            eval: this.slug,
            comparisons_per_model: this.comparisons,
        });
        try {
            const res = await fetch(`/_add-comparisons-price-preview/?${params.toString()}`);
            if (!res.ok) throw new Error(`Price fetch failed (${res.status})`);
            const data = await res.json();
            if (seq === this.priceFetchSeq) {
                this.serverPrice = data.price;
            }
        } catch (err) {
            // Keep the last known price rather than flashing wrong numbers.
            console.error('Add-comparisons price preview fetch failed:', err);
        }
    },

    formatCents(cents: number): string {
        return `$${(cents / 100).toFixed(2)}`;
    },
    // Survey top-ups price with calculate_survey_eval_price, whose dict carries
    // num_responses instead of num_comparisons; show whichever the price has so
    // the count in the panel matches the eval type's "additional responses/
    // comparisons" label.
    comparisonsCount(): number | string {
        if (!this.serverPrice) return '—';
        if (this.serverPrice.num_responses !== undefined) return this.serverPrice.num_responses;
        if (this.serverPrice.num_comparisons !== undefined) return this.serverPrice.num_comparisons;
        return '—';
    },
    totalCostDisplay(): string {
        return this.serverPrice ? this.formatCents(this.serverPrice.total_cents) : '—';
    },
    submitDisabled(): boolean {
        return !this.agreedToTerms || this.comparisons.length === 0 || this.serverPrice === null;
    },
    payButtonLabel(): string {
        if (!this.serverPrice) return 'Pay & add comparisons';
        return `Pay & add comparisons - ${this.formatCents(this.serverPrice.total_cents)}`;
    },
}));


Alpine.data('evalsList', () => ({
    search: '',
    clearSearch(): void {
        this.search = '';
        document.getElementById('evals-list-search')?.focus();
    },
    normalizeText(value: string | undefined): string {
        return (value || '')
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, ' ')
            .trim();
    },
    normalizedSearch(): string {
        return this.normalizeText(this.search);
    },
    hasSearch(): boolean {
        return this.normalizedSearch().length > 0;
    },
    matchesSearch(searchText: string | undefined): boolean {
        const normalizedSearch = this.normalizedSearch();

        if (!normalizedSearch) {
            return true;
        }

        return this.normalizeText(searchText).includes(normalizedSearch);
    },
    hasVisibleItems(): boolean {
        return Array
            .from(document.querySelectorAll<HTMLElement>('[data-evals-list-item]'))
            .some((item) => this.matchesSearch(item.dataset.search));
    }
}));

Alpine.start();


// Animations /////////////////////////////////////////////////////////////////////////////
export function initPageHeadingAnimations() {
    const fadeIn = document.querySelector<HTMLElement>('[data-page-heading-fade-in]');
    if (!fadeIn) return;

    const heading = fadeIn.querySelector<HTMLElement>('[data-page-heading]');
    const text = fadeIn.querySelector<HTMLElement>('[data-page-heading-text]');
    const timeline = gsap.timeline();

    if (heading) {
        timeline.fromTo(heading, {
            autoAlpha: 0,
        }, {
            autoAlpha: 1,
            duration: 0.9,
            ease: 'none',
        });
    }

    if (text) {
        timeline.fromTo(text, {
            autoAlpha: 0,
        }, {
            autoAlpha: 1,
            duration: 0.9,
            ease: 'none',
        }, '-=0.7');
    }
}

export function initAppIntroAnimations() {
    const appIntro = document.querySelector<HTMLElement>('[data-app-intro]');

    if (!appIntro) return;

    gsap.fromTo(appIntro, {
        autoAlpha: 0,
    }, {
        autoAlpha: 1,
        duration: 0.3,
        ease: 'none',
    });
}

export function initPanelFadeInAnimations() {
    const panels = gsap.utils.toArray<HTMLElement>('[data-panel-fade-in]');

    if (!panels.length) return;

    const timeline = gsap.timeline();

    // Animate panels and then clear transform for modal animations
    timeline.fromTo(panels, {
        autoAlpha: 0,
    }, {
        autoAlpha: 1,
        duration: 0.5,
        ease: 'none',
        stagger: 0.15,
    });

    timeline.fromTo(panels, {
        y: 8,
    }, {
        y: 0,
        duration: 0.7,
        ease: 'power2.out',
        stagger: 0.15,
        onComplete: () => {
            panels.forEach((panel) => {
                panel.style.transform = '';
            });
        },
    }, '0');
}

function fadeInPanels(panels: HTMLElement[], options: gsap.TimelineVars = {}) {
    const timeline = gsap.timeline(options);

    timeline.fromTo(panels, {
        autoAlpha: 0,
    }, {
        autoAlpha: 1,
        duration: 0.5,
        ease: 'none',
        stagger: 0.15,
    });

    timeline.fromTo(panels, {
        y: 8,
    }, {
        y: 0,
        duration: 0.7,
        ease: 'power2.out',
        stagger: 0.15,
        clearProps: 'transform',
    }, '0');

    return timeline;
}

export function initHowItWorksSectionFadeInAnimations() {
    const topNav = document.querySelector<HTMLElement>('[data-how-it-works-top-nav-fade-in]');
    const sections = gsap.utils.toArray<HTMLElement>('[data-how-it-works-section-fade-in]');
    const [initialSection, ...scrollSections] = sections;

    if (!topNav && !sections.length) return;

    const media = gsap.matchMedia();

    media.add('(prefers-reduced-motion: reduce)', () => {
        gsap.set([topNav, ...sections].filter(Boolean), { autoAlpha: 1, y: 0 });
    });

    media.add('(prefers-reduced-motion: no-preference)', () => {
        if (topNav) {
            const initialPanels = [topNav, initialSection].filter(Boolean);
            fadeInPanels(initialPanels, { delay: 0.2 });
        }

        scrollSections.forEach((section) => {
            fadeInPanels([section], {
                scrollTrigger: {
                    trigger: section,
                    start: 'top 90%',
                    once: true,
                },
            });
        });
    });
}

export function initEvalsListAnimations() {
    const items = gsap
        .utils
        .toArray<HTMLElement>('[data-evals-list-item]')
        .filter((item) => item.getClientRects().length > 0);

    if (!items.length) return;

    gsap.fromTo(items, {
        autoAlpha: 0,
        y: 4,
    }, {
        autoAlpha: 1,
        y: 0,
        duration: 0.4,
        stagger: 0.06,
        ease: 'power2.out',
    });
}

export function initEloBarAnimations() {
    const charts = gsap
        .utils
        .toArray<HTMLElement>('[data-elo-chart]')
        .filter((chart) => chart.getClientRects().length > 0);

    if (!charts.length) return;

    charts.forEach((chart) => {
        const reveals = gsap
            .utils
            .toArray<HTMLElement>(chart.querySelectorAll<HTMLElement>('[data-elo-bar-reveal]'))
            .filter((reveal) => reveal.getClientRects().length > 0);

        if (!reveals.length) return;

        const media = gsap.matchMedia();

        media.add('(prefers-reduced-motion: reduce)', () => {
            gsap.set(reveals, { width: '100%' });
        });

        media.add('(prefers-reduced-motion: no-preference)', () => {
            gsap.set(reveals, { width: '0%' });

            gsap.timeline({
                defaults: {
                    duration: 1.2,
                    ease: 'power2.inOut',
                },
                scrollTrigger: {
                    trigger: chart,
                    start: 'top 80%',
                    once: true,
                },
            }).to(reveals, {
                width: '100%',
                stagger: 0.08,
            });
        });
    });
}

export function initTextToImageCardAnimation() {
    const card = document.querySelector<HTMLElement>('[data-text-to-image-card]');

    if (!card) return;

    const prompts = gsap.utils.toArray<HTMLElement>(card.querySelectorAll('[data-text-to-image-prompt]'));
    const cardImages = gsap.utils.toArray<HTMLImageElement>(card.querySelectorAll('[data-text-to-image-card-image]'));
    const blurImages = gsap.utils.toArray<HTMLImageElement>(card.querySelectorAll('[data-text-to-image-blur-image]'));

    if (prompts.length < 2 || cardImages.length !== prompts.length || blurImages.length !== prompts.length) return;

    const media = gsap.matchMedia();

    media.add('(prefers-reduced-motion: reduce)', () => {
        gsap.set([prompts[0], cardImages[0]], { autoAlpha: 1, y: 0, scale: 1 });
        gsap.set(blurImages[0], { autoAlpha: 1, scale: 1.4 });
        gsap.set([...prompts.slice(1), ...cardImages.slice(1), ...blurImages.slice(1)], { autoAlpha: 0 });
    });

    media.add('(prefers-reduced-motion: no-preference)', () => {
        const timeline = gsap.timeline({
            repeat: -1,
        });

        gsap.set(prompts, { autoAlpha: 0, y: 0 });
        gsap.set(cardImages, { autoAlpha: 0, scale: 1 });
        gsap.set(blurImages, { autoAlpha: 0, scale: 1.5 });
        gsap.set([prompts[0], cardImages[0], blurImages[0]], { autoAlpha: 1 });

        const holdDuration = 2.0;

        prompts.forEach((_, index) => {
            const nextIndex = (index + 1) % prompts.length;
            const currentItems = [prompts[index], cardImages[index], blurImages[index]];
            const step = gsap.timeline();

            step
                .set(prompts[nextIndex], { autoAlpha: 0, y: 2 }, 0)
                .set(cardImages[nextIndex], { autoAlpha: 0, scale: 1.025 }, 0)
                .set(blurImages[nextIndex], { autoAlpha: 0, scale: 1.4 }, 0)
                .to(prompts[index], {
                    autoAlpha: 0,
                    y: -2,
                    duration: 0.4,
                    ease: 'power2.out',
                }, 0.02)
                .to(cardImages[index], {
                    autoAlpha: 0,
                    scale: 0.985,
                    duration: 1.7,
                    ease: 'power2.out',
                }, 0)
                .to(cardImages[nextIndex], {
                    autoAlpha: 1,
                    scale: 1,
                    duration: 2.5,
                    ease: 'power2.out',
                }, 0.12)
                .to(blurImages[index], {
                    autoAlpha: 0,
                    scale: 1.5,
                    duration: 1.4,
                    ease: 'power2.out',
                }, 0)
                .to(blurImages[nextIndex], {
                    autoAlpha: 1,
                    scale: 1.5,
                    duration: 1.4,
                    ease: 'power2.out',
                }, 0)
                .to(prompts[nextIndex], {
                    autoAlpha: 1,
                    y: 0,
                    duration: 0.48,
                    ease: 'power2.out',
                }, 0.22)
                .set(currentItems, { autoAlpha: 0 });

            timeline
                .to({}, { duration: holdDuration })
                .add(step);
        });
    });
}

function setGenerateActiveDot(dots: HTMLElement[], activeIndex: number) {
    dots.forEach((dot, index) => {
        const isActive = index === activeIndex;

        dot.classList.toggle('bg-foreground', isActive);
        dot.classList.toggle('border-foreground', isActive);
        dot.classList.toggle('bg-surface-strong', !isActive);
        dot.classList.toggle('border-border', !isActive);

        if (isActive) {
            dot.setAttribute('data-generate-prompt-dot-active', 'true');
            return;
        }

        dot.removeAttribute('data-generate-prompt-dot-active');
    });
}

const generateAnimationTiming = {
    promptDuration: 6,
    promptInDuration: 0.5,
    promptOutStart: 5.2,
    promptOutDuration: 0.5,
    imageStagger: 0.08,
    trackLoopDuration: 20,
};

const getGenerateOutputItems = (outputSet: HTMLElement): HTMLElement[] => {
    return gsap.utils.toArray<HTMLElement>(outputSet.querySelectorAll('[data-generate-output-item]'));
};

export function initHowItWorksGenerateAnimation() {
    const section = document.querySelector<HTMLElement>('[data-generate-animation]');

    if (!section) return;

    const prompts = gsap.utils.toArray<HTMLElement>(section.querySelectorAll('[data-generate-prompt]'));
    const models = gsap.utils.toArray<HTMLElement>(section.querySelectorAll('[data-generate-model]'));
    const outputSets = gsap.utils.toArray<HTMLElement>(section.querySelectorAll('[data-generate-output-set]'));
    const tracks = gsap.utils.toArray<HTMLElement>(section.querySelectorAll('[data-generate-output-track]'));
    const dots = gsap.utils.toArray<HTMLElement>(section.querySelectorAll('[data-generate-dot]'));

    const media = gsap.matchMedia();

    const showInitialGenerateState = () => {
        const [firstPrompt, ...inactivePrompts] = prompts;
        const [firstOutputSet, ...inactiveOutputSets] = outputSets;

        setGenerateActiveDot(dots, 0);
        gsap.set(firstPrompt, { autoAlpha: 1, y: 0 });
        gsap.set(inactivePrompts, { autoAlpha: 0 });
        gsap.set(models, { autoAlpha: 1, y: 0 });
        gsap.set(firstOutputSet, { autoAlpha: 1 });
        gsap.set(inactiveOutputSets, { autoAlpha: 0 });
        gsap.set(tracks, { xPercent: 0, x: 0 });
        gsap.set(getGenerateOutputItems(firstOutputSet), { autoAlpha: 1 });
        gsap.set(inactiveOutputSets.flatMap(getGenerateOutputItems), { autoAlpha: 0 });
    };

    media.add('(prefers-reduced-motion: reduce)', () => {
        showInitialGenerateState();
    });

    media.add('(prefers-reduced-motion: no-preference)', () => {
        const {
            promptDuration,
            promptInDuration,
            promptOutStart,
            promptOutDuration,
            imageStagger,
            trackLoopDuration,
        } = generateAnimationTiming;

        const timeline = gsap.timeline({
            repeat: -1,
            scrollTrigger: {
                trigger: section,
                start: 'top 85%',
                toggleActions: 'play none none none',
            },
        });

        gsap.set(prompts, { autoAlpha: 0, y: 2 });
        gsap.set(models, { autoAlpha: 1, y: 0 });
        gsap.set(outputSets, { autoAlpha: 0 });
        gsap.set(tracks, { xPercent: 0, x: 0 });
        setGenerateActiveDot(dots, 0);

        gsap.to(tracks, {
            xPercent: -50,
            duration: trackLoopDuration,
            ease: 'none',
            repeat: -1,
        });

        prompts.forEach((prompt, index) => {
            const outputSet = outputSets[index];
            const outputItems = getGenerateOutputItems(outputSet);
            const setStart = index * promptDuration;

            if (!outputItems.length) return;

            timeline
                .call(() => setGenerateActiveDot(dots, index), [], setStart)
                .set(prompt, { autoAlpha: 0, y: 2 }, setStart)
                .set(outputItems, { autoAlpha: 0 }, setStart)
                .to(outputSet, {
                    autoAlpha: 1,
                    duration: promptInDuration,
                    ease: 'none',
                }, setStart)
                .to(prompt, {
                    autoAlpha: 1,
                    y: 0,
                    duration: promptInDuration,
                    ease: 'power2.out',
                }, setStart)
                .to(outputItems, {
                    autoAlpha: 1,
                    duration: promptInDuration,
                    ease: 'none',
                    stagger: imageStagger,
                }, setStart)
                .to(prompt, {
                    autoAlpha: 0,
                    y: 0,
                    duration: promptOutDuration,
                    ease: 'power2.in',
                }, setStart + promptOutStart)
                .to(outputSet, {
                    autoAlpha: 0,
                    duration: promptOutDuration,
                    ease: 'none',
                }, setStart + promptOutStart);
        });
    });
}

export function initHowItWorksScrollNavAnimation() {
    const scrollNav = document.querySelector<HTMLElement>('[data-how-it-works-scroll-nav]');

    if (!scrollNav) return;

    const media = gsap.matchMedia();
    const triggerPoint = 450;
    const offscreenY = -120;

    media.add({
        isDesktop: '(min-width: 1024px)',
        reduceMotion: '(prefers-reduced-motion: reduce)',
    }, (context) => {
        const { isDesktop, reduceMotion } = context.conditions as {
            isDesktop: boolean;
            reduceMotion: boolean;
        };

        if (!isDesktop) return;

        const setNavVisible = (isVisible: boolean, animate = false) => {
            const vars = {
                autoAlpha: isVisible ? 1 : 0,
                y: isVisible || reduceMotion ? 0 : offscreenY,
                pointerEvents: isVisible ? 'auto' : 'none',
                overwrite: true,
            };

            if (!animate || reduceMotion) {
                gsap.set(scrollNav, vars);
                return;
            }

            gsap.to(scrollNav, {
                ...vars,
                duration: isVisible ? .5 : 0.3,
                ease: isVisible ? 'power2.out' : 'power2.in',
            });
        };

        setNavVisible(window.scrollY >= triggerPoint);

        const trigger = ScrollTrigger.create({
            start: triggerPoint,
            end: 'max',
            onEnter: () => setNavVisible(true, true),
            onLeaveBack: () => setNavVisible(false, true),
            onRefresh: () => setNavVisible(window.scrollY >= triggerPoint),
        });

        return () => {
            trigger.kill();
        };
    });
}

// Initialize animations
function initAnimations() {
    initAppIntroAnimations();
    initPageHeadingAnimations();
    initPanelFadeInAnimations();
    initHowItWorksSectionFadeInAnimations();
    initEvalsListAnimations();
    initEloBarAnimations();
    initTextToImageCardAnimation();
    initHowItWorksGenerateAnimation();
    initHowItWorksScrollNavAnimation();
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAnimations, { once: true });
} else {
    initAnimations();
}

